import {useConfig} from 'organization/Event/Configurable'
import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import {useTemplate} from 'Event/TemplateProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import Button from 'lib/ui/Button'
import GroupInput from 'lib/ui/form/GroupInput'
import TagsInput from 'lib/ui/form/TagsInput'
import {Group, Tag} from 'organization/Event/AttendeesProvider'
import React, {useState} from 'react'
import {Controller, UseFormMethods} from 'react-hook-form'
import Switch from 'lib/ui/form/Switch'
import {onChangeCheckedHandler} from 'lib/dom'
import styled from 'styled-components'
import Typography from '@material-ui/core/Typography'
import {SignUpAdditionalField} from 'Event/template'
import SignUpAdditionalFieldInput from 'Event/auth/SignUp/SignUpAdditionalFieldInput'

export default function PageSettings() {
  const {signUp} = useTemplate()
  const save = useSaveTemplate()

  const {
    form: {control, watch},
  } = useConfig()
  const hasPhoneNumberField = watch(
    'hasPhoneNumberField',
    signUp.hasPhoneNumberField,
  )

  const [tags, setTags] = useState<Tag[]>(signUp.tags || [])
  const [groups, setGroups] = useState<Group[]>(signUp.groups || [])
  const [additionalFields, setAdditionalFields] = useState<
    SignUpAdditionalField[]
  >(signUp.additionalFields || [])

  const addGroup = () => {
    setGroups([...groups, {key: '', value: ''}])
  }

  const updateGroup = (targetIndex: number) => (updated: Group) => {
    setGroups(groups.map((g, i) => (i === targetIndex ? updated : g)))
  }

  const removeGroup = (targetIndex: number) => () => {
    const updated = [...groups]
    updated.splice(targetIndex, 1)
    setGroups(updated)
  }

  const addAdditionalField = () => {
    setAdditionalFields([
      ...additionalFields,
      {groupKey: '', label: '', isRequired: false},
    ])
  }

  const updateAdditionalField = (targetIndex: number) => (
    updated: SignUpAdditionalField,
  ) => {
    setAdditionalFields(
      additionalFields.map((f, i) => (i === targetIndex ? updated : f)),
    )
  }

  const removeAdditionalField = (targetIndex: number) => () => {
    const updated = [...additionalFields]
    updated.splice(targetIndex, 1)
    setAdditionalFields(updated)
  }

  const handleSave = (updates: any) => {
    save({
      signUp: {
        ...updates,
        tags,
        groups,
        additionalFields,
      },
    })
  }

  return (
    <Config title="Sign Up Page Settings" onSave={handleSave}>
      <SettingsPanel>
        <Section>
          <SectionTitle>Collect phone number</SectionTitle>
          <SectionDescription>
            Ask attendees for a phone number when they register.
          </SectionDescription>
          <Controller
            name="hasPhoneNumberField"
            defaultValue={signUp.hasPhoneNumberField ?? false}
            control={control}
            render={({onChange, value}) => (
              <Switch
                checked={value}
                onChange={onChangeCheckedHandler(onChange)}
                aria-label="toggle collect phone number"
                labelPlacement="end"
                label="Collect Phone number"
              />
            )}
          />
          <PhoneNumberField control={control} hidden={!hasPhoneNumberField} />
        </Section>

        <Section>
          <SectionTitle>Shared tags and groups</SectionTitle>
          <SectionDescription>
            Tags and groups applied to every attendee who signs up through this
            page.
          </SectionDescription>
          <TagsInput
            value={tags}
            onChange={setTags}
            name="tags"
            aria-label="tags"
            label="Tags"
          />
          {groups.map((group, index) => (
            <GroupInput
              key={index}
              group={group}
              onChange={updateGroup(index)}
              remove={removeGroup(index)}
            />
          ))}
          <Button
            aria-label="add group"
            variant="outlined"
            color="primary"
            onClick={addGroup}
            fullWidth
            size="large"
          >
            Add Group
          </Button>
        </Section>

        <Section>
          <SectionTitle>Additional fields</SectionTitle>
          <SectionDescription>
            Extra inputs collected from each attendee at sign-up. Each field is
            saved as a group value.
          </SectionDescription>
          {additionalFields.map((field, index) => (
            <SignUpAdditionalFieldInput
              key={index}
              field={field}
              onChange={updateAdditionalField(index)}
              remove={removeAdditionalField(index)}
            />
          ))}
          <Button
            aria-label="add additional field"
            variant="outlined"
            color="primary"
            onClick={addAdditionalField}
            fullWidth
            size="large"
          >
            Add Field
          </Button>
        </Section>
      </SettingsPanel>
    </Config>
  )
}

function PhoneNumberField(props: {
  control: UseFormMethods['control']
  hidden: boolean
}) {
  const {control, hidden} = props

  const {signUp} = useTemplate()

  if (hidden) {
    return null
  }

  return (
    <Controller
      name="phoneNumberRequired"
      control={control}
      defaultValue={signUp.phoneNumberRequired ?? false}
      render={({value, onChange}) => (
        <Switch
          checked={value}
          onChange={onChangeCheckedHandler(onChange)}
          aria-label="toggle phone number required"
          labelPlacement="end"
          label="Phone number required"
        />
      )}
    />
  )
}

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[8]};

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled(Typography).attrs({variant: 'subtitle1'})`
  font-weight: 600;
`

const SectionDescription = styled(Typography).attrs({variant: 'body2'})`
  color: ${(props) => props.theme.colors.text.muted};
  margin-bottom: ${(props) => props.theme.spacing[3]};
`
